import Button from "@shared/components/ui-kit/form/Button/Button.web";
import Icon from "@shared/components/ui-kit/icons/Icon.web";
import { AuthProfileSerializer } from "@shared/modules/auth/serializer-types";
import FormErrorMessage from "@ui-kit/form/FormErrorMessage/FormErrorMessage.web";
import Input from "@ui-kit/form/Input/Input.web";
import Select from "@ui-kit/form/Select/Select.web";
import Textarea from "@ui-kit/form/Textarea/Textarea.web";

import usePublicVenueService from "@app-contexts/public/features/org-profile/data/services/usePublicVenueService";
import { PublicVenueSerializer } from "@app-contexts/public/features/org-profile/data/types/public-venue";
import Grid from "@ui-kit/layout/Grid/Grid.web";
import Link from "@ui-kit/navigation/Link/Link.web";
import FormControl from "@ui-kit/form/FormControl/FormControl.web";
import FormLabel from "@ui-kit/form/FormLabel/FormLabel.web";

import HStack from "@ui-kit/layout/Stack/HStack.web";
import VStack from "@ui-kit/layout/Stack/VStack.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import CaptchaBadgeStyler from "@shared/components/application/other/CaptchaBadgeStyler/CaptchaBadgeStyler";
import Tab from "@shared/components/ui-kit/disclosure/Tab/Tab.web";
import TabList from "@shared/components/ui-kit/disclosure/TabList/TabList.web";
import Tabs from "@shared/components/ui-kit/disclosure/BaseTabs/BaseTabs.web";
import { useMobileWebviewContext } from "@shared/contexts/MobileWebviewContext/MobileWebviewContext";
import useRecaptcha from "@shared/modules/auth/hooks/useRecaptcha";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { recordAppError } from "@shared/utils/errors/errorLogger";
import { Field, Form, Formik } from "formik";
import { useTranslation } from "@shared/utils/internationalization";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import * as yup from "yup";

export type ContactDetailsProps = {
  venueId?: number;
  displaySingleCol: boolean;
  onClose: () => void;
  setAllowsMessage: Dispatch<SetStateAction<boolean>>;
  user?: AuthProfileSerializer;
  isLoggedIn: boolean;
  refundPolicyText?: string;
};

const notifier = NotifierFactory.createNotifier();

const ContactDetails: React.FC<ContactDetailsProps> = ({
  venueId = undefined,
  displaySingleCol,
  onClose,
  setAllowsMessage,
  user = undefined,
  isLoggedIn,
  refundPolicyText = null,
}): JSX.Element => {
  const { t } = useTranslation("account");
  const [venueObject, setVenueObject] = useState<PublicVenueSerializer | null>(
    null
  );
  const [venueLinks, setVenueLinks] = useState<
    Array<{
      id: number;
      icon: JSX.Element;
      name: string;
      url?: string;
    }>
  >([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const { webviewValueAdapter } = useMobileWebviewContext();
  const { useRetrieve, useContactVenue } = usePublicVenueService();
  const { mutateAsync: contactVenue, isLoading } = useContactVenue(
    venueId as number
  );
  const { executeRecaptcha } = useRecaptcha(true);

  const tabOptions = [
    { title: t("Message Us") },
    { title: t("Refund Policy") },
  ];

  const selectOptions = [
    { id: 0, name: t("Request refund") },
    { id: 1, name: t("Question about my ticket") },
    { id: 2, name: t("Question about this event") },
    { id: 3, name: t("Question about the organizer") },
    { id: 4, name: t("Other") },
  ];

  const contactDetailsSchema = yup.object().shape({
    name: yup
      .string()
      .required(t("Name is required"))
      .min(2, t("Name must be at least 2 characters")),
    email: yup
      .string()
      .required(t("Email is required"))
      .email(t("Invalid email address")),
    event_name: yup
      .string()
      .required(t("Event name is required"))
      .min(2, t("Event name must be at least 2 characters")),
    subject: yup
      .string()
      .required(t("Reason for contact is required"))
      .nullable(),
    message: yup.string().required(t("Message is required")),
  });

  const { data: venueData } = useRetrieve(venueId as number);

  useEffect(() => {
    if (venueData) {
      const { web_address, facebook, twitter, instagram } = venueData;
      setVenueObject(venueData);
      setVenueLinks([
        {
          id: 0,
          icon: (
            <Icon.Website.Outline
              boxSize="element.lg"
              aria-label={t("Website")}
            />
          ),
          name: t("Website"),
          url: web_address,
        },
        {
          id: 1,
          icon: (
            <Icon.FacebookLogo
              boxSize="element.lg"
              aria-label={t("Facebook")}
            />
          ),
          name: "Facebook",
          url: facebook,
        },
        {
          id: 2,
          icon: <Icon.XLogo boxSize="element.lg" aria-label={t("Twitter")} />,
          name: "Twitter",
          url: twitter,
        },
        {
          id: 3,
          icon: (
            <Icon.InstagramLogo
              boxSize="element.lg"
              aria-label={t("Instagram")}
            />
          ),
          name: "Instagram",
          url: instagram,
        },
      ]);
    }
  }, [venueData, t]);

  useEffect(() => {
    setAllowsMessage(venueObject?.allow_messages_from_customers);
  }, [setAllowsMessage, venueObject]);

  const organizerInfo: JSX.Element = (
    <>
      <CaptchaBadgeStyler forceVisible />
      <Flex direction="column" width="100%">
        {!displaySingleCol && (
          <Text pb="2xl" fontSize="md" fontWeight="bold">
            {venueObject?.name ? (
              <Link
                href={`/o/${venueObject?.slug}/`}
                external
                target="_blank"
                variant="plain"
                fontWeight="bold"
                color="fg"
              >
                {venueObject?.name}
              </Link>
            ) : (
              t("Loading...")
            )}
          </Text>
        )}
        {venueObject && (
          <Flex direction="column" pb="2xl">
            <Text fontWeight="bold">{t("Refund Policy")}</Text>
            <Text pt="xs">
              {t(`${refundPolicyText || venueObject?.default_refund_policy}`) ||
                t("This venue hasn't specified a refund policy.")}
            </Text>
          </Flex>
        )}
      </Flex>

      <Grid
        width="100%"
        maxHeight="element.11xl"
        templateColumns={!displaySingleCol ? "repeat(4,1fr)" : "repeat(2,1fr)"}
        gap={!displaySingleCol ? "10px" : "30px"}
      >
        {venueLinks.map((link) => (
          <Button
            data-testid={link.name}
            style={webviewValueAdapter({ webview: { maxHeight: 160 } })}
            key={link.id}
            hidden={!link.url}
            cursor="pointer"
            display="flex"
            flexDirection="column"
            alignItems="center"
            width="100%"
            height="100%"
            maxHeight="element.6xl"
            p="lg"
            color="primary.900"
            backgroundColor="gray.200"
            _hover={{
              backgroundColor: "gray.300",
            }}
            borderRadius="md"
            fontSize="xs"
            fontWeight="default"
            onClick={() => window.open(link.url)}
          >
            <span>{link.icon}</span>
            <Text pt="2xs">{link.name}</Text>
          </Button>
        ))}
      </Grid>
    </>
  );

  const handleSubmit = async ({
    name,
    email,
    event_name,
    subject,
    message,
  }) => {
    const recaptchaToken = await executeRecaptcha(`contact_venue_${venueId}`);

    const params = {
      id: venueId,
      name,
      email,
      event_name,
      subject,
      message,
      captcha_token: recaptchaToken,
    };

    // contact venue api call
    contactVenue(params)
      .then(() => {
        notifier.success(t("Your message has been sent!"));

        // Close modal
        onClose();
      })
      .catch((error) => {
        recordAppError(error);
      });
  };

  const messageForm: JSX.Element = (
    <>
      <Text pb="lg" fontSize="md" fontWeight="bold">
        {t("Message Us")}
      </Text>
      <Formik
        initialValues={{
          name:
            user && isLoggedIn ? `${user.first_name} ${user.last_name}` : "",
          email: user && isLoggedIn ? user.email : "",
          event_name: "",
          subject: null,
          message: "",
        }}
        validationSchema={contactDetailsSchema}
        onSubmit={handleSubmit}
      >
        {(props) => (
          <Form style={{ width: "100%" }}>
            <Field name="name">
              {({ field, form }) => (
                <FormControl isInvalid={form.errors.name && form.touched.name}>
                  <FormLabel fontSize="sm" htmlFor="name">
                    {t("Name")}
                  </FormLabel>
                  <Input
                    {...field}
                    id="name"
                    placeholder={t("Your Name")}
                    minLength={2}
                  />
                  <FormErrorMessage>{form.errors.name}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
            <Box mb="lg" />
            <Field name="email">
              {({ field, form }) => (
                <FormControl
                  isInvalid={form.errors.email && form.touched.email}
                >
                  <FormLabel fontSize="sm" htmlFor="email">
                    {t("Email")}
                  </FormLabel>
                  <Input {...field} id="email" placeholder={t("Your Email")} />
                  <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
            <Box mb="lg" />
            <Field name="event_name">
              {({ field, form }) => (
                <FormControl
                  isInvalid={form.errors.event_name && form.touched.event_name}
                >
                  <FormLabel fontSize="sm" htmlFor="event_name">
                    {t("Event Name")}
                  </FormLabel>
                  <Input
                    {...field}
                    id="event_name"
                    placeholder={t("Event Name")}
                    minLength={2}
                  />
                  <FormErrorMessage>{form.errors.event_name}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
            <Box mb="lg" />
            <Field name="subject">
              {({ form }) => (
                <FormControl
                  isInvalid={form.errors.subject && form.touched.subject}
                >
                  <FormLabel htmlFor="subject" fontSize="sm">
                    {t("Reason for Contact")}
                  </FormLabel>
                  <Select
                    id="subject"
                    ariaLabel="select option"
                    placeholder={t("Select Option")}
                    variant="secondary"
                    items={selectOptions}
                    selectedItem={selectedOption}
                    isInvalid={form.errors.subject && form.touched.subject}
                    onSelectedItemChange={({ selectedItem }) => {
                      setSelectedOption(selectedItem);
                      props.setFieldValue("subject", selectedItem.name);
                    }}
                  />
                  <FormErrorMessage>{form.errors.subject}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
            <Box mb="lg" />
            <Field name="message">
              {({ field, form }) => (
                <FormControl
                  isInvalid={form.errors.message && form.touched.message}
                >
                  <FormLabel fontSize="sm" htmlFor="message">
                    {t("Message")}
                  </FormLabel>
                  <Textarea
                    {...field}
                    id="message"
                    placeholder={t("Your Message")}
                    maxLength={500}
                    maxHeight="element.11xl"
                  />
                  <FormErrorMessage>{form.errors.message}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
            <Box h="element.lg" />
            <Box h="element.lg" />
            <Flex justifyContent="flex-end">
              <Button
                data-testid="contact-details-send-button"
                minWidth="element.7xl"
                height="element.3xl"
                variant="solid"
                colorPalette="primary"
                isLoading={isLoading}
                disabled={isLoading}
                type="submit"
              >
                {t("Send")}
              </Button>
            </Flex>
          </Form>
        )}
      </Formik>
    </>
  );

  return (
    <>
      {displaySingleCol ? (
        <VStack width="100%">
          <Text width="100%" fontSize="md" fontWeight="bold">
            {venueObject?.name || t("Loading...")}
          </Text>
          {venueObject?.allow_messages_from_customers && (
            <Tabs
              pt="xs"
              width="100%"
              isManual
              index={selectedTab}
              onChange={(index) => {
                setSelectedTab(index);
              }}
            >
              <TabList>
                {tabOptions.map(({ title }) => {
                  return (
                    <Tab key={title} height="auto" p="none" mr="md">
                      <span>{title}</span>
                    </Tab>
                  );
                })}
              </TabList>
            </Tabs>
          )}
          <Flex pt="2xl" width="100%" direction="column">
            {selectedTab === 0 ? messageForm : organizerInfo}
          </Flex>
        </VStack>
      ) : (
        <HStack alignItems="flex-start" width="100%" height="100%">
          <VStack
            width={venueObject?.allow_messages_from_customers ? "50%" : "100%"}
            height="100%"
          >
            {organizerInfo}
          </VStack>
          {venueObject?.allow_messages_from_customers && (
            <>
              <Box width="element.4xl" />
              <VStack
                alignItems="flex-start"
                alignContent="flex-start"
                width="50%"
              >
                {messageForm}
              </VStack>
            </>
          )}
        </HStack>
      )}
    </>
  );
};

export default ContactDetails;
